import { useCallback, useLayoutEffect, useState } from 'react'

/**
 * Measures a sticky page header and returns the px offset that content below it
 * should pin at (`top`), or 0 when the header is not actually sticky.
 *
 * The offset cannot be hardcoded here. The client portal's header has no
 * declared height — it is intrinsic, and it differs by route (Navigation.desktop
 * adds a 3px active border on /adas-reports) and by breakpoint (html font-size
 * drops to 14px under 768px, rescaling every rem). More importantly, for share
 * visitors with no session Layout.header renders a plain, NON-sticky header, so
 * the correct offset in that case is 0 — pinning to its height would leave a
 * gap the size of the header.
 *
 * Previously this measured only inside a `scroll` listener, so `top` was 0 until
 * the user scrolled (the pinned element overlapped the nav on first paint) and
 * it never re-measured on resize.
 */
const useStrickyElement = elementId => {
  const [top, setTop] = useState(0)

  const measure = useCallback(() => {
    const el = document.getElementById(elementId)
    if (!el) {
      setTop(0)
      return
    }
    // A static header does not occupy the viewport as the page scrolls, so
    // anything pinning below it must use 0, not the header's height.
    const { position } = window.getComputedStyle(el)
    if (position !== 'sticky' && position !== 'fixed') {
      setTop(0)
      return
    }
    setTop(el.getBoundingClientRect().height)
  }, [elementId])

  useLayoutEffect(() => {
    measure()

    // The header wraps and changes height on resize, and the ADAS active-tab
    // border lands after the route resolves — observe rather than measure once.
    let observer
    if (typeof ResizeObserver !== 'undefined') {
      const el = document.getElementById(elementId)
      if (el) {
        observer = new ResizeObserver(measure)
        observer.observe(el)
      }
    }
    window.addEventListener('resize', measure)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [elementId, measure])

  return top
}

export default useStrickyElement
